import { RootState } from '@iwProvider/configureStore';
import { GetAccountsResponse } from '@iwoca/lapi-client/edge';
import _ from 'lodash';
import { TProductType } from '../../../myAccount/dashboard/Dashboard.types';
import {
    canRequestTopUp,
    canTopUpAfterRepayments,
    isPreApproved,
    isTopUpApproved,
    isTopUpDeclined,
    isTopUpPending
} from '../accountsSelector';
import { differenceInMonths } from 'date-fns';

type TAccount = NonNullable<GetAccountsResponse['data']>['accounts'][number];

export type TTopUpStatus =
    | 'NOT_YET_TOPUP_ELIGIBLE'
    | 'TOPUP_PENDING'
    | 'TOPUP_DECLINED'
    | 'TOPUP_ELIGIBLE'
    | 'TOPUP_APPROVED'
    | 'PRE_APPROVED'
    | null;

export const getTopUpStatus = (
    state: RootState,
    productType: TProductType
): TTopUpStatus => {
    const isNotYetTopUpEligible = canTopUpAfterRepayments(state, productType);
    const canTopUpNow = canRequestTopUp(state, productType);
    const topUpPending = isTopUpPending(state, productType);
    const topUpDeclined = isTopUpDeclined(state);
    const preApproved = isPreApproved(state, productType);
    const topUpApproved = isTopUpApproved(state, productType);

    const statusDate = state.api.application.data?.status_date;
    const declineStatus = state.api.application.data?.decline_status;

    const monthsSinceStatusChange = differenceInMonths(new Date(), statusDate);

    let hasDeclineExpired;

    if (
        (declineStatus === 'Soft Declined' && monthsSinceStatusChange >= 3) ||
        (declineStatus === 'Hard Declined' && monthsSinceStatusChange >= 6)
    ) {
        hasDeclineExpired = true;
    }

    if (preApproved) {
        if (topUpPending) {
            return 'TOPUP_PENDING';
        }

        if (topUpDeclined && !hasDeclineExpired) {
            return 'TOPUP_DECLINED';
        }

        return 'PRE_APPROVED';
    }

    if (topUpApproved) return 'TOPUP_APPROVED';
    if (topUpPending) return 'TOPUP_PENDING';
    if (isNotYetTopUpEligible) return 'NOT_YET_TOPUP_ELIGIBLE';
    if (topUpDeclined && !hasDeclineExpired) return 'TOPUP_DECLINED';
    if (canTopUpNow) return 'TOPUP_ELIGIBLE';

    return null;
};

export const getMaxMonthlyInterestRate = (account: TAccount) => {
    const rates =
        account.scheduled_payments!.summary!.rate_structures![0].rates;
    const maxRate = _.maxBy(rates, 'max_principal');

    return maxRate!.rate;
};
